import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Input, Select } from 'components/FormFields';

// GraphQL query for waivers
const GET_WAIVERS = gql`
  query GetWaiversData($type: String!) {
    getWaivers(type: $type) {
      id
      name
    }
  }
`;

// Form card container
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#64748B'};
  margin: -12px 0 20px 0;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr 1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Selection list container
const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SelectWrapper = styled.div`
  flex: 1;
`;

const RemoveButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 12px;

  button {
    width: auto;
    padding: 10px 20px;
  }
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#94A3B8'};
  margin: 0;
  padding: 16px;
  text-align: center;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  border: 1px dashed
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
`;

// Icons
const BuildingIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

const FileTextIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WaiversTab = ({ values, setFieldValue }) => {
  // Fetch all available waivers
  const { data: waiversData } = useQuery(GET_WAIVERS, {
    variables: { type: 'waiver' },
  });
  const allWaivers = waiversData?.getWaivers || [];

  // Fetch all available agreements
  const { data: agreementsData } = useQuery(GET_WAIVERS, {
    variables: { type: 'agreement' },
  });
  const allAgreements = agreementsData?.getWaivers || [];

  const handleChange = (field) => (e) => {
    setFieldValue(field, e.target.value);
  };

  // Helper to get ID from waiver object or string
  const getWaiverId = (waiver) => {
    if (typeof waiver === 'object' && waiver !== null) {
      return waiver.id?.toString() || '';
    }
    return waiver?.toString() || '';
  };

  // Waiver handlers
  const addWaiver = () => {
    const currentWaivers = values.waivers || [];
    setFieldValue('waivers', [...currentWaivers, { id: '', name: '' }]);
  };

  const removeWaiver = (index) => {
    const currentWaivers = values.waivers || [];
    setFieldValue(
      'waivers',
      currentWaivers.filter((_, i) => i !== index)
    );
  };

  const updateWaiver = (index, selectedId) => {
    const currentWaivers = values.waivers || [];
    const selectedWaiver = allWaivers.find(
      (w) => w.id.toString() === selectedId
    );
    const updated = [...currentWaivers];
    updated[index] = selectedWaiver
      ? { id: selectedWaiver.id, name: selectedWaiver.name }
      : { id: selectedId, name: '' };
    setFieldValue('waivers', updated);
  };

  // Agreement handlers
  const addAgreement = () => {
    const currentAgreements = values.agreements || [];
    setFieldValue('agreements', [...currentAgreements, { id: '', name: '' }]);
  };

  const removeAgreement = (index) => {
    const currentAgreements = values.agreements || [];
    setFieldValue(
      'agreements',
      currentAgreements.filter((_, i) => i !== index)
    );
  };

  const updateAgreement = (index, selectedId) => {
    const currentAgreements = values.agreements || [];
    const selectedAgreement = allAgreements.find(
      (a) => a.id.toString() === selectedId
    );
    const updated = [...currentAgreements];
    updated[index] = selectedAgreement
      ? { id: selectedAgreement.id, name: selectedAgreement.name }
      : { id: selectedId, name: '' };
    setFieldValue('agreements', updated);
  };

  // Get available waivers (exclude already selected ones)
  const getAvailableWaivers = (currentIndex) => {
    const selectedIds = (values.waivers || [])
      .filter((_, i) => i !== currentIndex)
      .map((w) => getWaiverId(w));
    return allWaivers.filter((w) => !selectedIds.includes(w.id.toString()));
  };

  // Get available agreements (exclude already selected ones)
  const getAvailableAgreements = (currentIndex) => {
    const selectedIds = (values.agreements || [])
      .filter((_, i) => i !== currentIndex)
      .map((a) => getWaiverId(a));
    return allAgreements.filter((a) => !selectedIds.includes(a.id.toString()));
  };

  return (
    <>
      {/* Insurance Company */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <BuildingIcon />
            Insurance Information
          </SectionTitle>
          <SectionSubtext>
            Enter the insurance company name associated with this account.
          </SectionSubtext>

          <FormRow>
            <FormGroup>
              <Input
                label="Insurance Company Name"
                value={values.insurance_company || ''}
                onChange={handleChange('insurance_company')}
                placeholder="Enter insurance company name"
              />
            </FormGroup>
            <FormGroup />
          </FormRow>
        </FormSection>
      </FormCard>

      {/* Waivers */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <FileTextIcon />
            Waivers
          </SectionTitle>
          <SectionSubtext>
            Select the waivers that apply to this account.
          </SectionSubtext>

          <SelectionList>
            {(values.waivers || []).length === 0 ? (
              <EmptyText>No waivers selected</EmptyText>
            ) : (
              (values.waivers || []).map((waiver, index) => (
                <SelectionRow key={index}>
                  <SelectWrapper>
                    <Select
                      value={getWaiverId(waiver)}
                      onChange={(e) => updateWaiver(index, e.target.value)}
                      options={[
                        { value: '', label: 'Select a waiver...' },
                        ...getAvailableWaivers(index).map((w) => ({
                          value: w.id.toString(),
                          label: w.name,
                        })),
                        // Include currently selected waiver even if it would be filtered
                        ...(getWaiverId(waiver)
                          ? allWaivers
                              .filter(
                                (w) => w.id.toString() === getWaiverId(waiver)
                              )
                              .map((w) => ({
                                value: w.id.toString(),
                                label: w.name,
                              }))
                          : []),
                      ].filter(
                        (opt, i, arr) =>
                          arr.findIndex((o) => o.value === opt.value) === i
                      )}
                    />
                  </SelectWrapper>
                  <RemoveButtonWrapper>
                    <ButtonV2
                      danger
                      variant="icon"
                      type="button"
                      onClick={() => removeWaiver(index)}
                      title="Remove waiver"
                      icon={<XIcon />}
                    />
                  </RemoveButtonWrapper>
                </SelectionRow>
              ))
            )}
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={addWaiver}
                icon={<PlusIcon />}
              >
                Add Waiver
              </ButtonV2>
            </AddButtonWrapper>
          </SelectionList>
        </FormSection>
      </FormCard>

      {/* Agreements */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <ClipboardIcon />
            Agreements
          </SectionTitle>
          <SectionSubtext>
            Select the agreements that apply to this account.
          </SectionSubtext>

          <SelectionList>
            {(values.agreements || []).length === 0 ? (
              <EmptyText>No agreements selected</EmptyText>
            ) : (
              (values.agreements || []).map((agreement, index) => (
                <SelectionRow key={index}>
                  <SelectWrapper>
                    <Select
                      value={getWaiverId(agreement)}
                      onChange={(e) => updateAgreement(index, e.target.value)}
                      options={[
                        { value: '', label: 'Select an agreement...' },
                        ...getAvailableAgreements(index).map((a) => ({
                          value: a.id.toString(),
                          label: a.name,
                        })),
                        // Include currently selected agreement even if it would be filtered
                        ...(getWaiverId(agreement)
                          ? allAgreements
                              .filter(
                                (a) =>
                                  a.id.toString() === getWaiverId(agreement)
                              )
                              .map((a) => ({
                                value: a.id.toString(),
                                label: a.name,
                              }))
                          : []),
                      ].filter(
                        (opt, i, arr) =>
                          arr.findIndex((o) => o.value === opt.value) === i
                      )}
                    />
                  </SelectWrapper>
                  <RemoveButtonWrapper>
                    <ButtonV2
                      danger
                      variant="icon"
                      type="button"
                      onClick={() => removeAgreement(index)}
                      title="Remove agreement"
                      icon={<XIcon />}
                    />
                  </RemoveButtonWrapper>
                </SelectionRow>
              ))
            )}
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={addAgreement}
                icon={<PlusIcon />}
              >
                Add Agreement
              </ButtonV2>
            </AddButtonWrapper>
          </SelectionList>
        </FormSection>
      </FormCard>
    </>
  );
};

WaiversTab.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default WaiversTab;
